/**
 * @file Labs module Settings surface.
 */

import { appData, tempConfigData } from "../../app/state/state.js";
import { bindNativeFieldBehavior } from "../../shared/forms/nativeFieldStyles.js";
import { createSelectControl } from "../../shared/ui/controls/selectControl.js";
import { createUserOptions } from "../../shared/ui/controls/controlOptions.js";
import { openConfirmationModal } from "../../shared/ui/confirmationModal.js";
import { escapeHTML } from "../../shared/utils/contentUtils.js";
import {
    bindStatusPrioritySettingsControls,
    renderCategoryEditor,
    renderOptionEditor,
} from "./statusPrioritySettings.js";
import {
    bindTypeSettingsControls,
    closeTypeSettingsDropdownsOnOutsideClick,
    renderTypeEditor,
} from "./typeSettings.js";

const LABS_SETTINGS_LAYOUT_CLASSES =
    "labs-settings-layout w-full max-w-[92rem] space-y-8";
const LABS_SETTINGS_CARD_CLASSES =
    "labs-settings-card bg-white p-6 rounded-lg shadow-md";
const LABS_STREAMS = Object.freeze([
    { value: "internal", label: "Internal" },
    { value: "external", label: "External" },
]);

let showArchivedExternalLabs = false;
let pendingExternalLabFocusId = null;

function attrValue(value = "") {
    return escapeHTML(String(value ?? ""));
}

function focusPendingExternalLabInput() {
    if (!pendingExternalLabFocusId) {return;}
    const targetId = pendingExternalLabFocusId;
    const index = getLabsConfig().externalLabs.findIndex((lab) =>
        lab?.id === targetId);
    if (index < 0) {return;}

    const input = document.querySelector(
        `[data-labs-external-index="${index}"]`,
    );
    if (!input) {return;}
    pendingExternalLabFocusId = null;
    input.focus();
    input.select();
}

function requestExternalLabFocus(id) {
    pendingExternalLabFocusId = id;
    requestAnimationFrame(() => {
        focusPendingExternalLabInput();
        if (pendingExternalLabFocusId) {
            setTimeout(focusPendingExternalLabInput, 0);
        }
    });
}

function getLabsConfig() {
    if (!tempConfigData.labs || typeof tempConfigData.labs !== "object") {
        tempConfigData.labs = {};
    }
    if (!Array.isArray(tempConfigData.labs.externalLabs)) {
        tempConfigData.labs.externalLabs = [];
    }
    if (
        !tempConfigData.labs.defaultAssignedUserByStream ||
        typeof tempConfigData.labs.defaultAssignedUserByStream !== "object"
    ) {
        tempConfigData.labs.defaultAssignedUserByStream = {};
    }
    LABS_STREAMS.forEach((stream) => {
        if (!Object.prototype.hasOwnProperty.call(
            tempConfigData.labs.defaultAssignedUserByStream,
            stream.value,
        )) {
            tempConfigData.labs.defaultAssignedUserByStream[stream.value] =
                null;
        }
    });
    return tempConfigData.labs;
}

function userCanBeDefaultAssignee(user, selectedValue) {
    if (!user) {return false;}
    const userId = user.id || user.uid;
    if (userId && String(userId) === String(selectedValue)) {return true;}
    if (user.isActive === false) {return false;}
    if (user.role === "admin") {return true;}
    return Array.isArray(user.permissions) &&
        user.permissions.includes("labs");
}

function getExternalClosedStatusOptions(selectedValue = "") {
    const selected = String(selectedValue || "");
    return (Array.isArray(tempConfigData.labStatus) ? tempConfigData.labStatus : [])
        .filter((status) => {
            const streams = Array.isArray(status?.streams) ?
                status.streams.map(String) :
                [];
            return status?.id &&
                status.isClosed === true &&
                (streams.length === 0 || streams.includes("external") ||
                    String(status.id) === selected);
        })
        .map((status) => ({
            value: String(status.id),
            label: String(status.name || status.id),
            color: status.color || "",
            selectedOnly: status.isClosed !== true,
            unavailableLabel: status.isClosed !== true ? "saved" : "",
        }));
}

function renderExternalLabOrderButton({index, direction, disabled}) {
    const icon = direction === "up" ? "expand_less" : "expand_more";
    const label = direction === "up" ? "Move external lab up" :
        "Move external lab down";
    const disabledAttr = disabled ? " disabled" : "";
    return `
        <button type="button" class="move-labs-external-lab-btn inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-50 hover:text-[var(--dfc-brand-primary)] focus:outline-none focus-visible:text-[var(--dfc-brand-primary)] disabled:cursor-not-allowed disabled:opacity-25 disabled:hover:bg-transparent disabled:hover:text-gray-400" data-index="${index}" data-direction="${direction}" title="${label}" aria-label="${label}"${disabledAttr}>
            <span class="material-symbols-outlined pointer-events-none text-[18px]">${icon}</span>
        </button>
    `;
}

function getSortedExternalLabEntries(externalLabs) {
    return externalLabs
        .map((lab, index) => ({
            index,
            lab,
            order: Number(lab?.order ?? index + 1) || index + 1,
        }))
        .filter((entry) => showArchivedExternalLabs ||
            entry.lab?.isActive !== false)
        .sort((a, b) => {
            const activeDiff = (a.lab?.isActive === false ? 1 : 0) -
                (b.lab?.isActive === false ? 1 : 0);
            if (activeDiff !== 0) {return activeDiff;}
            const orderDiff = a.order - b.order;
            return orderDiff !== 0 ? orderDiff : a.index - b.index;
        });
}

function getMaxActiveExternalLabOrder(externalLabs) {
    return Math.max(
        0,
        ...externalLabs
            .filter((lab) => lab?.isActive !== false)
            .map((lab) => Number(lab.order) || 0),
    );
}

function moveExternalLab(index, direction) {
    const externalLabs = getLabsConfig().externalLabs;
    const target = externalLabs[index];
    if (!target || typeof target !== "object") {return false;}

    const targetIsActive = target.isActive !== false;
    const entries = externalLabs
        .map((lab, labIndex) => ({
            index: labIndex,
            lab,
            order: Number(lab?.order ?? labIndex + 1) || labIndex + 1,
        }))
        .filter((entry) => (entry.lab?.isActive !== false) === targetIsActive)
        .sort((a, b) => {
            const orderDiff = a.order - b.order;
            return orderDiff !== 0 ? orderDiff : a.index - b.index;
        });
    const currentPosition = entries.findIndex((entry) => entry.index === index);
    const nextPosition = direction === "up"
        ? currentPosition - 1
        : currentPosition + 1;

    if (
        currentPosition < 0 ||
        nextPosition < 0 ||
        nextPosition >= entries.length
    ) {
        return false;
    }

    const current = entries[currentPosition].lab;
    const next = entries[nextPosition].lab;
    const currentOrder = Number(current.order) || entries[currentPosition].order;
    const nextOrder = Number(next.order) || entries[nextPosition].order;
    current.order = nextOrder;
    next.order = currentOrder;
    return true;
}

function renderExternalLabAction(entry) {
    const isActive = entry.lab?.isActive !== false;
    const hasBeenUsed = entry.lab?.hasBeenUsed === true;

    if (!hasBeenUsed) {
        return `
            <button type="button" class="delete-labs-external-lab-btn settings-config-delete" data-index="${entry.index}" title="Delete external lab" aria-label="Delete external lab">
                <span class="material-symbols-outlined text-lg">delete</span>
            </button>
        `;
    }

    return `
        <button type="button" class="archive-labs-external-lab-btn settings-config-archive ${isActive ? "" : "settings-config-restore"}" data-index="${entry.index}" title="${isActive ? "Archive external lab" : "Restore external lab"}" aria-label="${isActive ? "Archive external lab" : "Restore external lab"}">
            <span class="material-symbols-outlined text-lg">${isActive ? "archive" : "unarchive"}</span>
        </button>
    `;
}

function renderExternalLabEditor() {
    const config = getLabsConfig();
    const entries = getSortedExternalLabEntries(config.externalLabs);
    return `
        <div>
            <div class="settings-type-toolbar">
                <label class="block font-medium">External labs</label>
                <label class="inline-flex items-center gap-2 text-xs text-gray-600">
                    <input type="checkbox" data-labs-external-show-archived ${showArchivedExternalLabs ? "checked" : ""}>
                    <span>Show archived</span>
                </label>
            </div>
            <div class="labs-external-lab-list">
                ${entries.map((entry) => {
        const isActive = entry.lab?.isActive !== false;
        const sameStateEntries = entries.filter((item) =>
            (item.lab?.isActive !== false) === isActive);
        const stateIndex = sameStateEntries.findIndex((item) =>
            item.index === entry.index);
        const isFirst = stateIndex === 0;
        const isLast = stateIndex === sameStateEntries.length - 1;
        const disabledAttr = isActive ? "" : " disabled";
        return `
            <div class="labs-external-lab-row ${isActive ? "" : "opacity-75"}">
                <div class="settings-config-order-controls labs-external-lab-order" aria-label="External lab order controls">
                    ${renderExternalLabOrderButton({
                        index: entry.index,
                        direction: "up",
                        disabled: isFirst,
                    })}
                    ${renderExternalLabOrderButton({
                        index: entry.index,
                        direction: "down",
                        disabled: isLast,
                    })}
                </div>
                <input type="text" value="${attrValue(entry.lab?.name || "")}" data-labs-external-index="${entry.index}" class="settings-config-name labs-external-lab-name" placeholder="External lab name"${disabledAttr}>
                <div class="labs-external-lab-action">
                    ${renderExternalLabAction(entry)}
                </div>
            </div>
        `;
    }).join("")}
            </div>
            <button type="button" class="add-labs-external-lab-btn settings-add-row-btn mt-3">+ Add External Lab</button>
        </div>
    `;
}

function renderLabBucketSettings() {
    const config = getLabsConfig();
    return `
        <div>
            <label class="block font-medium mb-2">In-house Lab Label</label>
            <input type="text" value="${attrValue(config.internalLabLabel || "")}" data-labs-internal-label class="settings-config-name max-w-md" placeholder="Internal lab">
            <p class="settings-helper-text mt-2">Blank uses the fallback label Internal lab.</p>
        </div>
    `;
}

function renderLabsCloseFlowSettings() {
    return `
        <div>
            <label class="block font-medium mb-2">External Lab Close Status</label>
            <div data-labs-close-status-control></div>
            <p class="settings-helper-text mt-2">This closed status requires Cost before an External Lab can close. Clearing the status disables the cost intercept.</p>
        </div>
    `;
}

function renderLabsDefaultAssignmentSettings() {
    return `
        <div class="grid gap-4 md:grid-cols-2">
            ${LABS_STREAMS.map((stream) => `
                <div>
                    <label class="block font-medium mb-2">Default ${stream.label} Assigned User</label>
                    <div data-labs-default-assignee-control="${stream.value}"></div>
                    <p class="settings-helper-text mt-2">Used for new ${stream.label.toLowerCase()} Labs items when active. Blank falls back to the current user.</p>
                </div>
            `).join("")}
        </div>
    `;
}

/**
 * Renders the Labs Settings tab.
 * @param {HTMLElement} container Settings tab content container.
 * @param {{ updateSaveButton?: Function }} [options={}] Render dependencies.
 * @returns {void}
 */
export function renderLabsSettingsTab(container, options = {}) {
    const { updateSaveButton = () => {} } = options;
    getLabsConfig();
    if (!Array.isArray(tempConfigData.labStatus)) {
        tempConfigData.labStatus = [];
    }
    if (!Array.isArray(tempConfigData.labCategoryConfig)) {
        tempConfigData.labCategoryConfig = [];
    }
    if (!Array.isArray(tempConfigData.labTypes)) {
        tempConfigData.labTypes = [];
    }

    container.innerHTML = `
        <div class="${LABS_SETTINGS_LAYOUT_CLASSES}">
            <div class="${LABS_SETTINGS_CARD_CLASSES}">
                <h4 class="text-lg font-semibold mb-4 border-b pb-2">Labs Status Groups</h4>
                ${renderCategoryEditor("Status Groups", "labCategoryConfig", tempConfigData.labCategoryConfig)}
            </div>
            <div class="${LABS_SETTINGS_CARD_CLASSES}">
                <h4 class="text-lg font-semibold mb-4 border-b pb-2">Labs Statuses</h4>
                ${renderOptionEditor("Status Options", "labStatus", tempConfigData.labStatus, true, tempConfigData.labCategoryConfig)}
            </div>
            <div class="${LABS_SETTINGS_CARD_CLASSES}">
                <h4 class="text-lg font-semibold mb-4 border-b pb-2">Labs Types</h4>
                ${renderTypeEditor("Types", "labTypes", tempConfigData.labTypes)}
            </div>
            <div class="${LABS_SETTINGS_CARD_CLASSES}">
                <h4 class="text-lg font-semibold mb-4 border-b pb-2">Lab Buckets</h4>
                <div class="space-y-6">
                    ${renderLabBucketSettings()}
                    ${renderExternalLabEditor()}
                </div>
            </div>
            <div class="${LABS_SETTINGS_CARD_CLASSES}">
                <h4 class="text-lg font-semibold mb-4 border-b pb-2">Close Flow</h4>
                ${renderLabsCloseFlowSettings()}
            </div>
            <div class="${LABS_SETTINGS_CARD_CLASSES}">
                <h4 class="text-lg font-semibold mb-4 border-b pb-2">Defaults</h4>
                ${renderLabsDefaultAssignmentSettings()}
            </div>
        </div>
    `;

    bindStatusPrioritySettingsControls(container);
    bindTypeSettingsControls(container);
    bindLabsSettingsControls(container, { updateSaveButton });
    bindNativeFieldBehavior(container);

    document.removeEventListener("click", closeTypeSettingsDropdownsOnOutsideClick);
    document.addEventListener("click", closeTypeSettingsDropdownsOnOutsideClick);
}

function bindLabsSettingsControls(container, { updateSaveButton = () => {} } = {}) {
    const config = getLabsConfig();
    const closeStatusRoot = container.querySelector("[data-labs-close-status-control]");
    if (closeStatusRoot) {
        const selected = config.closeInterceptStatusId || "";
        createSelectControl({
            root: closeStatusRoot,
            accessibleName: "External Lab close status",
            value: selected,
            options: getExternalClosedStatusOptions(selected),
            clearable: true,
            portalMenu: true,
            placeholder: "Select closed status",
            mirror: false,
            onChange: (value) => {
                config.closeInterceptStatusId = value || null;
                updateSaveButton(true);
            },
        });
    }

    container.querySelectorAll("[data-labs-default-assignee-control]")
        .forEach((root) => {
            const stream = root.dataset.labsDefaultAssigneeControl;
            const selectedDefault =
                config.defaultAssignedUserByStream?.[stream] || "";
            const users = (appData.users || [])
                .filter((user) =>
                    userCanBeDefaultAssignee(user, selectedDefault))
                .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
            const options = createUserOptions(users, {
                selectedValues: selectedDefault ? [selectedDefault] : [],
            });

            createSelectControl({
                root,
                accessibleName: `Default ${stream} Lab assigned user`,
                value: selectedDefault,
                options,
                clearable: true,
                searchable: options.length >= 7,
                searchThreshold: 7,
                portalMenu: true,
                placeholder: "Current user fallback",
                mirror: false,
                onChange: (value) => {
                    config.defaultAssignedUserByStream[stream] = value || null;
                    updateSaveButton(true);
                },
            });
    });
}

/**
 * Handles Labs Settings input changes.
 * @param {HTMLElement} target Event target.
 * @param {{ updateSaveButton?: Function, rerenderCurrentTabContent?: Function }} [options={}] Handler dependencies.
 * @returns {boolean} True when handled.
 */
export function handleLabsSettingsChange(target, options = {}) {
    const {
        rerenderCurrentTabContent = () => {},
        updateSaveButton = () => {},
    } = options;

    if (target.matches("[data-labs-external-show-archived]")) {
        showArchivedExternalLabs = target.checked;
        rerenderCurrentTabContent();
        return true;
    }

    if (target.matches("[data-labs-internal-label]")) {
        getLabsConfig().internalLabLabel = target.value.trim() || null;
        updateSaveButton(true);
        return true;
    }

    if (target.matches("[data-labs-external-index]")) {
        const index = parseInt(target.dataset.labsExternalIndex, 10);
        const externalLab = getLabsConfig().externalLabs[index];
        if (!externalLab || externalLab.isActive === false) {return true;}
        externalLab.name = target.value;
        updateSaveButton(true);
        return true;
    }

    return false;
}

/**
 * Handles Labs Settings button actions.
 * @param {HTMLElement} target Event target.
 * @param {{ updateSaveButton?: Function, rerenderCurrentTabContent?: Function }} [options={}] Handler dependencies.
 * @returns {boolean} True when handled.
 */
export function handleLabsSettingsButtonClick(target, options = {}) {
    const {
        rerenderCurrentTabContent = () => {},
        updateSaveButton = () => {},
    } = options;
    const config = getLabsConfig();

    if (target.closest(".add-labs-external-lab-btn")) {
        const id = `external_lab_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
        config.externalLabs.push({
            id,
            name: "",
            isActive: true,
            order: getMaxActiveExternalLabOrder(config.externalLabs) + 1,
            hasBeenUsed: false,
        });
        updateSaveButton(true);
        rerenderCurrentTabContent();
        requestExternalLabFocus(id);
        return true;
    }

    if (target.closest(".move-labs-external-lab-btn")) {
        const btn = target.closest(".move-labs-external-lab-btn");
        const index = parseInt(btn.dataset.index, 10);
        if (moveExternalLab(index, btn.dataset.direction)) {
            updateSaveButton(true);
            rerenderCurrentTabContent();
        }
        return true;
    }

    if (target.closest(".archive-labs-external-lab-btn")) {
        const btn = target.closest(".archive-labs-external-lab-btn");
        const index = parseInt(btn.dataset.index, 10);
        const externalLab = config.externalLabs[index];
        if (!externalLab || typeof externalLab !== "object") {return true;}
        if (externalLab.hasBeenUsed !== true) {return true;}

        if (externalLab.isActive === false) {
            externalLab.isActive = true;
            externalLab.order = getMaxActiveExternalLabOrder(
                config.externalLabs,
            ) + 1;
        } else {
            externalLab.isActive = false;
        }
        updateSaveButton(true);
        rerenderCurrentTabContent();
        return true;
    }

    if (target.closest(".delete-labs-external-lab-btn")) {
        const btn = target.closest(".delete-labs-external-lab-btn");
        const index = parseInt(btn.dataset.index, 10);
        const externalLab = config.externalLabs[index];
        if (!externalLab || typeof externalLab !== "object") {return true;}
        if (externalLab.hasBeenUsed === true) {return true;}

        openConfirmationModal(
            "Delete External Lab?",
            "This External lab has not been used by any Labs item. Delete it from the settings list?",
            () => {
                config.externalLabs.splice(index, 1);
                updateSaveButton(true);
                rerenderCurrentTabContent();
            },
        );
        return true;
    }

    return false;
}
